/**
 * Sesion del backoffice, compartida por todas sus pantallas.
 *
 * Es un contexto y no un hook suelto porque la sesion la necesitan a la vez el
 * guardia de la ruta, la barra lateral (para saludar y ofrecer salir) y la
 * pantalla de administradores. Con un hook por componente, cada uno preguntaria
 * por su cuenta quien esta conectado.
 *
 * Al montar comprueba contra la API si el token guardado sigue valido, en vez
 * de confiar en lo que hay en el navegador: un token puede estar vencido, o la
 * cuenta desactivada desde otro equipo.
 *
 * El contexto y el hook viven en `sesion-contexto.ts` (ver el porque alli).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { Administrador, CredencialesDto } from '@elcarpintero/compartido';

import { guardarToken, obtenerToken } from '../servicios/cliente-api';
import { abrirSesion, obtenerSesion } from '../servicios/autenticacion.servicio';
import { ContextoSesion, type EstadoSesion, type ValorSesion } from './sesion-contexto';

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<EstadoSesion>(() =>
    obtenerToken() !== null ? 'comprobando' : 'fuera',
  );
  const [administrador, setAdministrador] = useState<Administrador | null>(null);

  useEffect(() => {
    if (obtenerToken() === null) return;

    const control = new AbortController();

    obtenerSesion(control.signal)
      .then((datos) => {
        if (control.signal.aborted) return;
        setAdministrador(datos);
        setEstado('dentro');
      })
      .catch(() => {
        if (control.signal.aborted) return;
        // El token guardado ya no sirve: se descarta en vez de dejar al panel
        // chocando con un 401 en cada pantalla.
        guardarToken(null);
        setAdministrador(null);
        setEstado('fuera');
      });

    return () => {
      control.abort();
    };
  }, []);

  const entrar = useCallback(async (credenciales: CredencialesDto) => {
    const acceso = await abrirSesion(credenciales);
    guardarToken(acceso.token);
    setAdministrador(acceso.administrador);
    setEstado('dentro');
  }, []);

  const salir = useCallback(() => {
    // El token no se guarda en el servidor: salir es que el navegador lo olvide.
    guardarToken(null);
    setAdministrador(null);
    setEstado('fuera');
  }, []);

  const valor = useMemo<ValorSesion>(
    () => ({ estado, administrador, entrar, salir, actualizar: setAdministrador }),
    [estado, administrador, entrar, salir],
  );

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>;
}
